import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { CATEGORIES, categoryLabel } from './categories';
import { useAuth } from './AuthContext';
import ConfirmDialog from './ConfirmDialog';
import { formatAdded, formatBytes, formatDocDate } from './format';
import './admin.css';

const PAGE_SIZE = 20;
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';
const emptyUploadForm = { category: CATEGORIES[0].value, title: '', year: '', document_date: '', file: null };

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'nodate', label: 'Missing date' },
];

export default function AdminDocuments() {
  const { checkSession } = useAuth();
  const [searchParams] = useSearchParams();

  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState('loading');

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => {
    const requested = searchParams.get('filter');
    return STATUS_FILTERS.some((f) => f.value === requested) ? requested : 'all';
  });
  const [page, setPage] = useState(1);

  const [uploadForm, setUploadForm] = useState(emptyUploadForm);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = setTimeout(() => setToast(null), 4500);
  }, []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // Session expiry (idle timeout, server restart, etc.) surfaces as a 401 on
  // whatever admin action is in flight. Re-sync auth state whenever that
  // happens so AdminProtectedRoute redirects to login instead of the page
  // just showing a permanent, unexplained failure.
  const throwIfSessionExpired = useCallback(
    (res) => {
      if (res.status === 401) {
        checkSession();
        throw new Error(SESSION_EXPIRED_MESSAGE);
      }
    },
    [checkSession]
  );

  const loadDocuments = useCallback(() => {
    // Only show the full-page loading state on the very first load; after
    // that, refresh quietly so the table doesn't flash on every action.
    setStatus((s) => (s === 'success' ? s : 'loading'));
    return fetch(apiUrl('/documents.php?all=1'), { credentials: 'include' })
      .then((res) => {
        throwIfSessionExpired(res);
        return res.json();
      })
      .then((data) => {
        if (!data || data.success !== true) throw new Error();
        setDocuments(Array.isArray(data.documents) ? data.documents : []);
        setStatus('success');
      })
      .catch(() => setStatus('error'));
  }, [throwIfSessionExpired]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (filterCategory && d.category !== filterCategory) return false;
      if (statusFilter === 'active' && !d.is_active) return false;
      if (statusFilter === 'inactive' && d.is_active) return false;
      if (statusFilter === 'nodate' && d.document_date) return false;
      if (q && !`${d.title} ${d.filename}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [documents, search, filterCategory, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  // ---- Upload ----
  const pickFile = (file) => {
    setUploadError('');
    setUploadForm((f) => {
      const next = { ...f, file: file || null };
      // Save a step: if no title has been typed yet, suggest one from the filename.
      if (file && !f.title.trim()) next.title = file.name.replace(/\.pdf$/i, '');
      return next;
    });
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    setUploadError('');

    if (!uploadForm.file) {
      setUploadError('Please choose a PDF file.');
      return;
    }
    if (uploadForm.file.type !== 'application/pdf' && !uploadForm.file.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Only PDF files are allowed.');
      return;
    }
    if (!uploadForm.document_date) {
      setUploadError('Document date is required so the site can sort by it.');
      return;
    }

    const body = new FormData();
    body.append('category', uploadForm.category);
    body.append('title', uploadForm.title);
    body.append('year', uploadForm.year);
    body.append('document_date', uploadForm.document_date);
    body.append('file', uploadForm.file);

    setUploading(true);
    try {
      const res = await fetch(apiUrl('/upload.php'), {
        method: 'POST',
        credentials: 'include',
        body,
      });
      throwIfSessionExpired(res);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Upload failed.');
      }
      showToast('success', 'Document uploaded successfully.');
      setUploadForm({ ...emptyUploadForm, category: uploadForm.category });
      if (fileInputRef.current) fileInputRef.current.value = '';
      setPage(1);
      await loadDocuments();
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  // ---- Edit ----
  const openEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      title: doc.title,
      year: doc.year || '',
      document_date: doc.document_date || '',
      category: doc.category,
      display_order: doc.display_order,
      is_active: doc.is_active,
    });
    setEditError('');
  };

  const closeEdit = useCallback(() => {
    setEditingDoc(null);
    setEditForm(null);
    setEditError('');
  }, []);

  useEffect(() => {
    if (!editingDoc) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeEdit();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editingDoc, closeEdit]);

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingDoc) return;
    setSavingEdit(true);
    setEditError('');
    try {
      const res = await fetch(apiUrl('/update.php'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editingDoc.id,
          title: editForm.title,
          year: editForm.year,
          document_date: editForm.document_date,
          category: editForm.category,
          display_order: Number(editForm.display_order) || 0,
          is_active: !!editForm.is_active,
        }),
      });
      throwIfSessionExpired(res);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to save changes.');
      closeEdit();
      showToast('success', 'Changes saved.');
      await loadDocuments();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes.');
    } finally {
      setSavingEdit(false);
    }
  };

  // ---- Deactivate / Activate / Delete (each behind a confirm dialog) ----
  const runToggle = async (doc) => {
    try {
      const res = await fetch(apiUrl('/update.php'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: doc.id, is_active: !doc.is_active }),
      });
      throwIfSessionExpired(res);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Action failed.');
      showToast('success', doc.is_active ? 'Document deactivated.' : 'Document activated.');
      await loadDocuments();
    } catch (err) {
      showToast('error', err.message || 'Action failed.');
    }
  };

  const runDelete = async (doc) => {
    try {
      const res = await fetch(apiUrl('/delete.php'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: doc.id, mode: 'delete' }),
      });
      throwIfSessionExpired(res);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed.');
      showToast('success', 'Document deleted.');
      await loadDocuments();
    } catch (err) {
      showToast('error', err.message || 'Delete failed.');
    }
  };

  const askToggle = (doc) =>
    setConfirm({
      title: doc.is_active ? 'Deactivate this document?' : 'Activate this document?',
      message: doc.is_active
        ? `"${doc.title}" will no longer appear on the public website. The file is kept, and you can activate it again any time.`
        : `"${doc.title}" will appear on the public website again.`,
      confirmLabel: doc.is_active ? 'Deactivate' : 'Activate',
      danger: false,
      run: () => runToggle(doc),
    });

  const askDelete = (doc) =>
    setConfirm({
      title: 'Delete this document?',
      message: `"${doc.title}" and its PDF file will be permanently removed. This cannot be undone.`,
      confirmLabel: 'Delete permanently',
      danger: true,
      run: () => runDelete(doc),
    });

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      await confirm.run();
    } finally {
      setConfirmBusy(false);
      setConfirm(null);
    }
  };

  const cancelConfirm = useCallback(() => setConfirm(null), []);

  const hasFilters = search.trim() !== '' || filterCategory !== '' || statusFilter !== 'all';

  return (
    <>
      <div className="adm-page-head">
        <div>
          <h1 className="adm-title">Documents</h1>
          <p className="adm-subtitle">Upload and manage the PDFs shown on the public website.</p>
        </div>
      </div>

      {/* ---------- Upload ---------- */}
      <div className="adm-card">
        <div className="adm-card-head">
          <h2 className="adm-card-title">Upload a document</h2>
        </div>
        <form className="adm-card-body" onSubmit={handleUploadSubmit}>
          {uploadError && (
            <div className="adm-alert adm-alert-error" role="alert">
              <i className="bi bi-exclamation-circle" />
              <span>{uploadError}</span>
            </div>
          )}

          <div className="adm-form-grid">
            <label className="adm-field span-4">
              <span className="adm-label">Category</span>
              <select
                className="adm-input"
                value={uploadForm.category}
                onChange={(e) => setUploadForm({ ...uploadForm, category: e.target.value })}
                required
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="adm-field span-4">
              <span className="adm-label">Document date</span>
              <input
                className="adm-input"
                type="date"
                value={uploadForm.document_date}
                onChange={(e) => setUploadForm({ ...uploadForm, document_date: e.target.value })}
                required
              />
            </label>

            <label className="adm-field span-4">
              <span className="adm-label">
                Year / period <em className="adm-optional">(optional)</em>
              </span>
              <input
                className="adm-input"
                type="text"
                placeholder="e.g. 2026 - 2027"
                value={uploadForm.year}
                onChange={(e) => setUploadForm({ ...uploadForm, year: e.target.value })}
              />
            </label>

            <label className="adm-field span-12">
              <span className="adm-label">Title</span>
              <input
                className="adm-input"
                type="text"
                placeholder="e.g. Annual Report (2026 - 2027)"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                required
              />
            </label>

            <div className="span-12">
              <label
                className={`adm-drop${dragging ? ' is-dragging' : ''}${uploadForm.file ? ' has-file' : ''}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
                  if (dropped) pickFile(dropped);
                }}
              >
                <input
                  ref={fileInputRef}
                  className="adm-sr-only"
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={(e) => pickFile(e.target.files[0])}
                />
                <span className="adm-drop-icon">
                  <i className={`bi ${uploadForm.file ? 'bi-file-earmark-check' : 'bi-cloud-arrow-up'}`} />
                </span>
                <span className="adm-drop-text">
                  {uploadForm.file ? (
                    <>
                      <strong>{uploadForm.file.name}</strong>
                      <span>{formatBytes(uploadForm.file.size)} · click to choose a different file</span>
                    </>
                  ) : (
                    <>
                      <strong>Choose a PDF or drag it here</strong>
                      <span>PDF files only, up to 50 MB</span>
                    </>
                  )}
                </span>
              </label>
            </div>
          </div>

          <div className="adm-form-actions">
            <button type="submit" className="adm-btn adm-btn-primary" disabled={uploading}>
              <i className="bi bi-cloud-arrow-up" />
              {uploading ? 'Uploading…' : 'Upload & publish'}
            </button>
          </div>
        </form>
      </div>

      {/* ---------- List ---------- */}
      <div className="adm-card adm-card-spaced">
        <div className="adm-card-head">
          <h2 className="adm-card-title">All documents</h2>
          {status === 'success' && (
            <span className="adm-muted">
              {filtered.length === documents.length
                ? `${documents.length} total`
                : `${filtered.length} of ${documents.length}`}
            </span>
          )}
        </div>

        <div className="adm-toolbar">
          <div className="adm-search">
            <i className="bi bi-search" />
            <input
              className="adm-input"
              type="search"
              placeholder="Search by title or filename"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              aria-label="Search documents"
            />
          </div>
          <select
            className="adm-input adm-select-auto"
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setPage(1);
            }}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            className="adm-input adm-select-auto"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            aria-label="Filter by status"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button
              type="button"
              className="adm-btn adm-btn-ghost"
              onClick={() => {
                setSearch('');
                setFilterCategory('');
                setStatusFilter('all');
                setPage(1);
              }}
            >
              Clear
            </button>
          )}
        </div>

        {status === 'loading' && (
          <div className="adm-state">
            <span className="adm-spinner" /> Loading documents…
          </div>
        )}
        {status === 'error' && (
          <div className="adm-state">
            <i className="bi bi-exclamation-circle" /> Unable to load documents. Please try again.
          </div>
        )}
        {status === 'success' && filtered.length === 0 && (
          <div className="adm-empty">
            <i className="bi bi-inbox" />
            <p>{hasFilters ? 'No documents match your filters.' : 'No documents available.'}</p>
          </div>
        )}

        {status === 'success' && filtered.length > 0 && (
          <>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="adm-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <div className="adm-doc-title">{doc.title}</div>
                        <div className="adm-doc-sub">Added {formatAdded(doc.created_at)}</div>
                      </td>
                      <td>
                        <span className="adm-pill adm-pill-gray">{categoryLabel(doc.category)}</span>
                      </td>
                      <td className="adm-nowrap">
                        {doc.document_date ? (
                          formatDocDate(doc.document_date)
                        ) : (
                          <span className="adm-pill adm-pill-amber">No date</span>
                        )}
                      </td>
                      <td>
                        <span className={`adm-pill adm-pill-dot ${doc.is_active ? 'adm-pill-green' : 'adm-pill-gray'}`}>
                          {doc.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="adm-actions">
                          <a
                            className="adm-icon-btn"
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Preview PDF"
                            aria-label={`Preview ${doc.title}`}
                          >
                            <i className="bi bi-box-arrow-up-right" />
                          </a>
                          <button
                            type="button"
                            className="adm-icon-btn"
                            onClick={() => openEdit(doc)}
                            title="Edit"
                            aria-label={`Edit ${doc.title}`}
                          >
                            <i className="bi bi-pencil-square" />
                          </button>
                          <button
                            type="button"
                            className="adm-icon-btn"
                            onClick={() => askToggle(doc)}
                            title={doc.is_active ? 'Deactivate' : 'Activate'}
                            aria-label={`${doc.is_active ? 'Deactivate' : 'Activate'} ${doc.title}`}
                          >
                            <i className={`bi ${doc.is_active ? 'bi-eye-slash' : 'bi-eye'}`} />
                          </button>
                          <button
                            type="button"
                            className="adm-icon-btn is-danger"
                            onClick={() => askDelete(doc)}
                            title="Delete"
                            aria-label={`Delete ${doc.title}`}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="adm-pager">
                <span>
                  Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className="adm-pager-controls">
                  <button
                    type="button"
                    className="adm-btn"
                    onClick={() => setPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    <i className="bi bi-chevron-left" /> Previous
                  </button>
                  <span className="adm-pager-page">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="adm-btn"
                    onClick={() => setPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    Next <i className="bi bi-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ---------- Edit modal ---------- */}
      {editingDoc && editForm && (
        <div
          className="adm-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeEdit();
          }}
        >
          <form className="adm-modal" onSubmit={saveEdit} role="dialog" aria-modal="true" aria-labelledby="adm-edit-title">
            <div className="adm-modal-head">
              <h2 id="adm-edit-title" className="adm-modal-title">
                Edit document
              </h2>
              <button type="button" className="adm-icon-btn" onClick={closeEdit} aria-label="Close">
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="adm-modal-scroll">
              {editError && (
                <div className="adm-alert adm-alert-error" role="alert">
                  <i className="bi bi-exclamation-circle" />
                  <span>{editError}</span>
                </div>
              )}

              <label className="adm-field">
                <span className="adm-label">Title</span>
                <input
                  className="adm-input"
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                />
              </label>

              <div className="adm-form-grid adm-form-grid-2">
                <label className="adm-field">
                  <span className="adm-label">Document date</span>
                  <input
                    className="adm-input"
                    type="date"
                    value={editForm.document_date}
                    onChange={(e) => setEditForm({ ...editForm, document_date: e.target.value })}
                  />
                  {!editForm.document_date && (
                    <small className="adm-hint is-warn">No date set — this will sort after all dated documents.</small>
                  )}
                </label>

                <label className="adm-field">
                  <span className="adm-label">Year / period</span>
                  <input
                    className="adm-input"
                    type="text"
                    value={editForm.year}
                    onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
                  />
                </label>

                <label className="adm-field">
                  <span className="adm-label">Category</span>
                  <select
                    className="adm-input"
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="adm-field">
                  <span className="adm-label">Display order</span>
                  <input
                    className="adm-input"
                    type="number"
                    value={editForm.display_order}
                    onChange={(e) => setEditForm({ ...editForm, display_order: e.target.value })}
                  />
                  <small className="adm-hint">Only used to order documents that share the same date.</small>
                </label>
              </div>

              <label className="adm-check">
                <input
                  type="checkbox"
                  checked={!!editForm.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                />
                <span>Active — visible on the public website</span>
              </label>
            </div>

            <div className="adm-modal-foot">
              <button type="button" className="adm-btn" onClick={closeEdit}>
                Cancel
              </button>
              <button type="submit" className="adm-btn adm-btn-primary" disabled={savingEdit}>
                {savingEdit ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        busy={confirmBusy}
        onConfirm={handleConfirm}
        onCancel={cancelConfirm}
      />

      {toast && (
        <div className={`adm-toast is-${toast.type}`} role="status" aria-live="polite">
          <i className={`bi ${toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} />
          <span>{toast.message}</span>
        </div>
      )}
    </>
  );
}
